// 파트너센터 「약관 서명 열기」 스위치 — app_config.partner_center_signing_open.enabled (2026-09-11, 기획 docs/ops/partner-center-access-plan.md §5-2)
//
// 약관 3문서가 확정되기 전까지 파트너센터는 **둘러보기**(읽기 전용)만 연다. 서버 RPC `partner_center_signing_open()`(로그인 계정)이 값을 주고,
// 어드민 「파트너 관리」 맨 위 카드가 `admin_set_partner_center_signing_open(boolean)` 으로 바꾼다.
//
// 🔒 규칙
//   · 실패하면 **닫힘**(fail-closed). RPC 가 없거나(마이그레이션 전) 네트워크가 죽어도 서명 단추가 튀어나오지 않는다 — 대표 결정 ③ 「그 전엔 전원 둘러보기」.
//   · 어드민 겸직도 스위치를 따른다 — 「어드민 확인용」은 파트너가 보는 화면 그대로여야 한다(크리에이터 노출 스위치와 다른 점).
//   · 값은 세션 동안 한 번만 읽고 모듈 캐시에 둔다. 어드민이 스위치를 바꾸면 `setPartnerSigningOpenCache` 로 즉시 반영.
//   · 이 스위치는 「서명 단추가 열리느냐」만 정한다. 서명 RPC·원장 판정은 서버가 종전대로 처리한다.
//
// 훅은 src/hooks/usePartnerSigningOpen.js — 여기는 순수 함수 + fetch 만(테스트: partnerSigningOpen.test.js).
import { supabase } from "./supabaseClient";
import { tt } from "../i18n/tt";

export const PARTNER_SIGNING_OPEN_RPC = "partner_center_signing_open";

let cache = null; // { enabled: boolean, error: string|null }
let inflight = null;

/** RPC 응답 → boolean. true 일 때만 열린 것으로 본다 */
export function normalizeSigningOpen(data) {
  return data === true;
}

/**
 * 둘러보기 모드인가 — 화면 표시 상태(원장이 아니다). 서버 status 가 pending(약관 서명 전)이면 둘러보기.
 * 스위치는 「서명 단추가 열리느냐」만 바꾸고, 둘러보기 여부 자체는 바꾸지 않는다.
 */
export function isBrowsing(stage) {
  return !stage || stage === "pending";
}

/** 둘러보기 띠의 문안·단추 — 스위치에 따라 「서명하기」 또는 「약관 미리 읽기」 */
export function browseBandCopy({ signingOpen }) {
  return signingOpen
    ? { bodyKey: "a.76df947f", bodyKo: tt("a.76df947f", "약관에 서명하면 파트너 코드와 정산이 열립니다."), ctaKey: "a.63f1846f", ctaKo: tt("a.63f1846f", "서명하기"), cta: "sign" }
    : { bodyKey: "a.268b4f88", bodyKo: tt("a.268b4f88", "약관 서명이 준비되면 알려드릴게요. 서명하면 파트너 코드와 정산이 열립니다."), ctaKey: "a.baaaa7c6", ctaKo: tt("a.baaaa7c6", "약관 미리 읽기"), cta: "preview" };
}

export function getPartnerSigningOpenCache() {
  return cache;
}

export function setPartnerSigningOpenCache(enabled) {
  cache = { enabled: enabled === true, error: null };
  return cache;
}

/** 테스트용 */
export function resetPartnerSigningOpenCache() {
  cache = null;
  inflight = null;
}

/** 스위치 값을 한 번 읽는다. 실패하면 { enabled:false, error } — 절대 throw 하지 않는다 */
export function fetchPartnerSigningOpen({ force = false } = {}) {
  if (cache && !force) return Promise.resolve(cache);
  if (inflight && !force) return inflight;
  inflight = Promise.resolve()
    .then(() => supabase.rpc(PARTNER_SIGNING_OPEN_RPC))
    .then(({ data, error }) => {
      cache = error
        ? { enabled: false, error: String(error.message || error) }
        : { enabled: normalizeSigningOpen(data), error: null };
      return cache;
    })
    .catch((e) => {
      cache = { enabled: false, error: String(e?.message || e) };
      return cache;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}
